import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getDiscountsRequest } from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type { DiscountsResponse } from "Api/billingSystem/types";

// Thunks
export const getDiscounts = createAppAsyncThunk(
  "billingSystem/discounts/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getDiscountsRequest(billingProfileId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type DiscountsState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: DiscountsResponse;
      error?: string;
    }
  >;
};

const initialState: DiscountsState = {
  byProfileId: {}
};

// Slice
const discountsSlice = createSlice({
  name: "discounts",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Get discounts by profile
    builder
      .addCase(getDiscounts.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getDiscounts.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          action.payload
        );
      })
      .addCase(getDiscounts.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });
  }
});

export default discountsSlice.reducer;

// Selectors
const discountsStateSelector = (state: RootState) => state.discounts;

// Discounts by profile ID
export const selectProfileDiscounts = createSelector(
  discountsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.data
);

export const selectProfileDiscountsLoading = createSelector(
  discountsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.byProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileDiscountsError = createSelector(
  discountsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.error
);

// Individual discount list selector
export const selectProfileDiscountsList = createSelector(
  selectProfileDiscounts,
  data => data?.items
);
